<template>
    <div class="event__edit_completions">
        <component-title :size="'medium'">{{ $t('ebs.events.edit.title.completion') }}</component-title>
        <form-radio
            :label="$t('ebs.events.edit.labels.calculateEventCompletionOn')"
            :name="'calculate-event-completion'"
            :items="[
                {
                    name: this.$t('ebs.events.edit.name.attendance'),
                    value: 'attendance'
                }
            ]"
            :hints="[this.$t('ebs.events.edit.title.hintsEvent')]"
            :value="eventCompletionType === 'attendance' || eventCompletionType === 'automatic' ? 'attendance' : eventCompletionType"
            @input="setEventCompletionType"
        />
        <transition-group name="fade" mode="out-in" tag="div">
            <template v-if="eventCompletionType === 'attendance' || eventCompletionType === 'automatic'">
                <component-spacing :margin-top="'-3rem'" :key="'component-spacing'"/>
                <form-radio
                    :key="'calculate-event-completion'"
                    :label="''"
                    :name="'calculate-event-completion'"
                    :items="[
                        {
                            name: this.$t('ebs.events.edit.name.whenSessionEnds'),
                            value: 'automatic'
                        },
                        {
                            name: this.$t('ebs.events.edit.name.whenAllRegisters'),
                            value: 'attendance'
                        }
                    ]"
                    :overlay="true"
                    v-model="eventCompletionType"
                />
            </template>
        </transition-group>
        <component-spacing :margin-top="'-2rem'"/>
        <form-radio
            :label="''"
            :name="'calculate-event-completion'"
            :items="[
                {
                    name: this.$t('ebs.events.edit.name.trainerAssessment'),
                    value: 'assessment'
                }
            ]"
            :hints="[
                    this.$t('ebs.events.edit.name.learnersAreMarked')
                ]"
            v-model="eventCompletionType"
        />
        <component-title :size="'medium'">{{ $t('ebs.events.edit.title.registers') }}</component-title>
        <form-checkbox
            :disabled="eventCompletionType === 'attendance' || eventCompletionType === 'automatic'"
            :name="'enable-registers'"
            :label="$t('ebs.events.edit.labels.enableRegisters')"
            v-model="eventCompletionEnableRegisters"
        >{{ $t('ebs.events.edit.title.registerAttendance') }}
        </form-checkbox>
        <component-title :size="'small'">{{ $t('ebs.events.edit.title.whoCan') }}</component-title>
        <form-checkbox
            :name="'event-coordinators'"
            :label="$t('ebs.events.edit.labels.eventCoordinators')"
            :value="true"
            :disabled="true"
        />
        <component-spacing :margin-top="'-2.5rem'"/>
        <form-radio
            :label="''"
            :name="'event-coordinators-any'"
            :items="[
                        {
                            name: this.$t('ebs.events.edit.name.assignedToThisEvent'),
                            value: 'false'
                        },
                        {
                            name: this.$t('ebs.events.edit.name.anyEventCoordinator'),
                            value: 'true'
                        }
                    ]"
            :overlay="true"
            v-model="eventCompletionRegistersEditByAnyCoordinator"
        />
        <component-spacing :margin-top="'-3rem'"/>
        <form-checkbox
            :disabled="!eventOccurrenceEnableTrainers"
            :name="'trainers'"
            :label="$t('ebs.events.edit.title.trainers')"
            v-model="eventCompletionRegistersEditByTrainers"
        />
        <component-title :size="'medium'">{{ $t('ebs.events.edit.title.absenceReason') }}</component-title>
        <form-checkbox
            :label="$t('ebs.events.edit.title.askAbsenceReason')"
            :name="'absence-booking-reason'"
            :hint="$t('ebs.events.edit.title.askWhenAbsenceReason')"
            v-model="eventCompletionAbsenceReasonRequired"
        />
        <transition-group name="fade" mode="out-in">
            <form-textarea
                :key="'absence-booking-reason-items'"
                v-if="eventCompletionAbsenceReasonRequired"
                :label="$t('ebs.events.edit.labels.absenceReason')"
                :name="'absence-booking-reason-items'"
                :required="true"
                :data-vv-as="$t('forms.dataVvAs.absenceReason')"
                :hint="$t('ebs.events.edit.name.addNewReason')"
                :v-validate="'required'"
                v-model="eventCompletionAbsenceReason"
            >{{ $t('ebs.events.edit.name.addNewReason') }}
            </form-textarea>
        </transition-group>
        <component-title :size="'medium'">{{ $t('ebs.events.edit.title.feedback') }}</component-title>
        <form-checkbox
            :disabled="!eventOccurrenceEnableTrainers"
            :label="$t('ebs.events.edit.labels.askTrainersToLeave')"
            :name="'ask-trainers-leave-feedback'"
            v-model="eventCompletionFormRequired"
        >{{ $t('ebs.events.edit.title.trainersWillLeave') }}
        </form-checkbox>
        <transition name="fade" mode="out-in">
            <template
                v-if="eventCompletionFormRequired"
            >
                <form-customform
                    :title="$t('ebs.events.edit.title.configureFeedback')"
                    :items="eventCompletionFormFields"
                    @emit-notify-add="eventAddCompletionFormField"
                    @emit-notify-edit="eventEditCompletionFormField"
                    @emit-notify-remove="eventRemoveCompletionFormField"
                    @emit-move="eventMoveCompletionFormField"
                    :previewTitle="'Trainer feedback'"
                    :previewSubtitle="'Please complete the form below'"
                    :name="'event-configure-feedback'"/>
            </template>
        </transition>
        <form-submit
            :submit-button-animate-loading="!eventHasOccurrences"
            @reset="eventReset"
            @submit="beforeEventSubmit"
        />
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
        <pop-notify
            ref="popnotifynotaffectsession"
            :class="'pop__notify--notaffectsession'"
            :title-text="'Save changes?'"
            :content-text="'Your changes will not affect the existing sessions of this event.'"
            :cancel-button-text="$t('ebs.events.edit.notify.buttonClose')"
            :submit-button-text="$t('ebs.events.edit.notify.buttonConfirm')"
            :prevent-close="true"
            @emit-submit="eventSubmit"
        />
    </div>
</template>

<script>
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormRadio from '@/components/default/forms/FormRadio'
import FormCustomform from '@/components/default/forms/FormCustomform'
import FormSubmit from '@/components/default/forms/FormSubmit'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import ComponentSpacing from '@/components/default/shared/ComponentSpacing'
import FormTextarea from '@/components/default/forms/FormTextarea'
import { watchContentChangeEmit } from '@/helpers/mixins'

export default {
  components: {
    FormTextarea,
    ComponentSpacing,
    ComponentTitle,
    PopNotify,
    FormSubmit,
    FormCustomform,
    FormRadio,
    FormCheckbox
  },
  name: 'event-edit-completions',
  mixins: [watchContentChangeEmit],
  computed: {
    eventOccurrenceEnableTrainers: {
      get () {
        return this.$store.getters['getEventOccurrenceEnableTrainers']
      }
    },
    //
    eventCompletionType: {
      get () {
        return this.$store.getters['getEventCompletionType']
      },
      set (value) {
        this.$store.dispatch('setEventCompletionType', value)
      }
    },
    eventCompletionEnableRegisters: {
      get () {
        return this.$store.getters['getEventCompletionEnableRegisters']
      },
      set (value) {
        this.$store.dispatch('setEventCompletionEnableRegisters', value)
      }
    },
    eventCompletionRegistersEditByAnyCoordinator: {
      get () {
        return this.$store.getters['getEventCompletionRegistersEditByAnyCoordinator']
      },
      set (value) {
        this.$store.dispatch('setEventCompletionRegistersEditByAnyCoordinator', value)
      }
    },
    eventCompletionRegistersEditByTrainers: {
      get () {
        return this.$store.getters['getEventCompletionRegistersEditByTrainers']
      },
      set (value) {
        this.$store.dispatch('setEventCompletionRegistersEditByTrainers', value)
      }
    },
    eventCompletionFormRequired: {
      get () {
        return this.$store.getters['getEventCompletionFormRequired']
      },
      set (value) {
        this.$store.dispatch('setEventCompletionFormRequired', value)
      }
    },
    eventCompletionFormFields: {
      get () {
        return this.$store.getters['getEventCompletionFormFields']
      }
    },
    eventCompletionAbsenceReasonRequired: {
      get () {
        return this.$store.getters['getEventCompletionAbsenceReasonRequired']
      },
      set (value) {
        this.$store.dispatch('setEventCompletionAbsenceReasonRequired', value)
      }
    },
    eventCompletionAbsenceReason: {
      get () {
        return this.$store.getters['getEventCompletionAbsenceReason']
      },
      set (value) {
        this.$store.dispatch('setEventCompletionAbsenceReason', value)
      }
    },
    watchContent () {
      return this.eventOccurrenceEnableTrainers + this.eventCompletionType + this.eventCompletionEnableRegisters +
      this.eventCompletionRegistersEditByAnyCoordinator + this.eventCompletionRegistersEditByTrainers + this.eventCompletionFormRequired +
      this.eventCompletionFormFields + this.eventCompletionAbsenceReasonRequired + this.eventCompletionAbsenceReason
    },
    eventAllowEdit: function () {
      return this.$store.getters.getEventAllowEdit
    },
    eventAllowEditStatus: function () {
      return this.$store.getters.getEventAllowEditStatus
    },
    eventHasOccurrences () {
      return this.$store.getters.getEventHasOccurrences
    },
    eventCustomRequestLoaded () {
      return this.$store.getters.getEventCustomRequestLoaded
    }
  },
  methods: {
    validateBeforeRouteLeave: function (to) {
      if (to.name.includes('ebs-event-edit') || to.name.includes('ebs-event-add')) {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.$parent.validated = true
            this.$router.push({ path: to.path })
          } else {
            this.$refs.popnotifyvalidation.openNotify()
          }
        })
      } else {
        this.$parent.validated = true
        this.$router.push({ path: to.path })
      }
    },
    allowRedirectBeforeRouteLeave: function () {
      this.$eventBus.$emit('event-submit')
    },
    beforeEventSubmit () {
      if (this.eventHasOccurrences) {
        this.openNotifyNotAffectSessions()
      } else {
        this.eventSubmit()
      }
    },
    openNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.openNotify()
    },
    closeNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.closeNotify()
    },
    checkEventStatus (value) {
      if (value === 'active' && this.eventHasOccurrencesDraft) {
        this.$refs.popnotifyhasdraftsessions.openNotify()
      }
    },
    //
    setEventCompletionType (value) {
      this.$store.dispatch('setEventCompletionType', value)
    },
    eventAddCompletionFormField: function (data) {
      this.$store.dispatch('setEventCompletionFormField', data)
    },
    eventEditCompletionFormField: function (data, index) {
      this.$store.dispatch('updateEventCompletionFormField', { data: data, index: index })
    },
    eventRemoveCompletionFormField: function (data) {
      this.$store.dispatch('removeEventCompletionFormField', data)
    },
    eventMoveCompletionFormField: function (data) {
      this.$store.dispatch('moveEventCompletionFormField', data)
    },
    //
    eventReset: function () {
      this.$store.dispatch('resetEvent')
    },
    eventSubmit: function () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.$route.name.includes('ebs-event-add')) {
            this.$store.dispatch('createEvent')
          } else if (this.$route.name.includes('ebs-event-edit')) {
            this.$store.dispatch('updateEvent')
          }
          this.allowRedirectBeforeRouteLeave()
        } else {
          this.$refs.popnotifyvalidation.openNotify()
        }
      })
    }
  },
  watch: {
    eventCustomRequestLoaded (value) {
      if (value) {
        this.closeNotifyNotAffectSessions()
      }
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>
